import { AppRouteRecordRaw } from '../types';
import Body from '@/components/body.vue';
import PayOperationsOverview from '@/pages/ryd-pay-operations/overview/PayOperationsOverview.vue';
import OrderAmendmentsGuide from '@/pages/ryd-pay-operations/order-amendments/guide/OrderAmendmentsGuide.vue';
import OrderAmendmentsQueue from '@/pages/ryd-pay-operations/order-amendments/queue/OrderAmendmentsQueue.vue';
import OrderAmendmentDetail from '@/pages/ryd-pay-operations/order-amendments/detail/OrderAmendmentDetail.vue';
import OrderAmendmentsAudit from '@/pages/ryd-pay-operations/order-amendments/audit/OrderAmendmentsAudit.vue';
import BulkJobDetail from '@/pages/ryd-pay-operations/order-amendments/bulk/BulkJobDetail.vue';
import IncidentsPage from '@/pages/ryd-pay-operations/order-amendments/incidents/IncidentsPage.vue';
import DriveAwayFlow from '@/pages/ryd-pay-operations/order-amendments/drive-away/DriveAwayFlow.vue';
import OdooPaymentUpdate from '@/pages/ryd-pay-operations/order-amendments/odoo-payment-update/OdooPaymentUpdate.vue';
import ManualAssignmentPage from '@/pages/ryd-pay-operations/order-amendments/manual-assignment/ManualAssignmentPage.vue';
import ManualAssignmentSessionStats from '@/pages/ryd-pay-operations/order-amendments/manual-assignment/SessionStatistics.vue';
import '@/assets/scss/pages/_order-amendments.scss';
import { Roles } from '@/constants/roles';
import { PARTNER_TYPES } from '@/constants/partner-types';
import { getUserAddDataFromLocalStorage } from '@/utils/userUtils';
import { hasReceiptAccess } from '@/services/ryd-pay-operations/receipt-upload/receipt-upload.helpers';

const PAY_OPS_ALL_ROLES = [
  Roles.pay_operations_viewer,
  Roles.pay_operations_agent,
  Roles.pay_operations_admin,
];

const PAY_OPS_AGENT_OR_ADMIN = [Roles.pay_operations_agent, Roles.pay_operations_admin];

const PAY_OPS_ADMIN_ONLY = [Roles.pay_operations_admin];

const RECEIPT_UPLOAD_ROLES = [
  Roles.customer_support_agent,
  Roles.customer_support_admin,
  Roles.pay_operations_agent,
  Roles.pay_operations_admin,
];

export const rydPayOperationsRoutes: AppRouteRecordRaw[] = [
  {
    path: '/pay-operations',
    component: Body,
    meta: {
      requiresAuth: true,
      requiredUserGroup: ['ryd'],
      breadcrumb: 'RYD Pay Operations',
    },
    children: [
      {
        path: 'receipt-upload',
        name: 'pay-operations-receipt-upload',
        component: () => import('@/pages/ryd-pay-operations/receipt-upload/ReceiptUpload.vue'),
        beforeEnter: () => hasReceiptAccess(getUserAddDataFromLocalStorage()) || '/unauthorized',
        meta: {
          title: 'Receipt Upload | ryd Portal',
          breadcrumb: 'Receipt Upload',
          requiredAnyRole: RECEIPT_UPLOAD_ROLES,
          strictRoles: true,
          requiredUserGroup: ['ryd'],
          requiredPartnerTypes: [PARTNER_TYPES.RYD_INTERNAL],
        },
      },
      {
        path: '',
        redirect: '/pay-operations/overview',
      },
      {
        path: 'overview',
        name: 'pay-operations-overview',
        component: PayOperationsOverview,
        meta: {
          title: 'ryd Pay Operations Overview | ryd Portal',
          breadcrumb: 'Overview',
          requiredRoles: PAY_OPS_ALL_ROLES,
        },
      },
      {
        path: 'order-amendments',
        redirect: '/pay-operations/order-amendments/queue',
      },
      {
        path: 'order-amendments/queue',
        name: 'pay-operations-order-amendments',
        component: OrderAmendmentsQueue,
        meta: {
          title: 'Order Amendments Queue | ryd Portal',
          breadcrumb: 'Order Amendments',
          requiredRoles: PAY_OPS_ALL_ROLES,
        },
      },
      {
        // The overview moved to the section root; keep old deep links working.
        path: 'order-amendments/overview',
        redirect: '/pay-operations/overview',
      },
      {
        path: 'order-amendments/guide',
        name: 'pay-operations-order-amendments-guide',
        component: OrderAmendmentsGuide,
        meta: {
          title: 'Order Amendments — Guide | ryd Portal',
          breadcrumb: 'Guide',
          requiredRoles: PAY_OPS_ALL_ROLES,
        },
      },
      {
        path: 'order-amendments/audit',
        name: 'pay-operations-order-amendments-audit',
        component: OrderAmendmentsAudit,
        meta: {
          title: 'Amendment Audit Log | ryd Portal',
          breadcrumb: 'Amendment Audit',
          requiredRoles: PAY_OPS_ADMIN_ONLY,
        },
      },
      {
        path: 'order-amendments/incidents',
        name: 'pay-operations-order-amendments-incidents',
        component: IncidentsPage,
        meta: {
          title: 'Incidents | ryd Portal',
          breadcrumb: 'Incidents',
          requiredRoles: PAY_OPS_ALL_ROLES,
        },
      },
      {
        path: 'order-amendments/bulk-jobs/:id',
        name: 'pay-operations-bulk-job-detail',
        component: BulkJobDetail,
        props: true,
        meta: {
          title: 'Bulk Job Detail | ryd Portal',
          breadcrumb: 'Bulk Job',
          requiredRoles: PAY_OPS_ALL_ROLES,
        },
      },
      {
        path: 'order-amendments/:uuid/drive-away',
        name: 'pay-operations-drive-away',
        component: DriveAwayFlow,
        props: true,
        meta: {
          title: 'Drive Away Flow | ryd Portal',
          breadcrumb: 'Drive Away',
          requiredRoles: PAY_OPS_AGENT_OR_ADMIN,
        },
      },
      {
        path: 'order-amendments/odoo-payment-update',
        name: 'pay-operations-odoo-payment-update',
        component: OdooPaymentUpdate,
        meta: {
          title: 'Update Odoo Invoice Payment | ryd Portal',
          breadcrumb: 'Update Odoo Invoice Payment',
          requiredRoles: PAY_OPS_AGENT_OR_ADMIN,
        },
      },
      {
        path: 'order-amendments/manual-assignment',
        name: 'pay-operations-manual-assignment',
        component: ManualAssignmentPage,
        meta: {
          title: 'Manual Order Assignment | ryd Portal',
          breadcrumb: 'Manual Assignment',
          requiredRoles: PAY_OPS_AGENT_OR_ADMIN,
        },
      },
      {
        path: 'order-amendments/manual-assignment/sessions/:sessionId',
        name: 'pay-operations-manual-assignment-session',
        component: ManualAssignmentSessionStats,
        props: true,
        meta: {
          title: 'Assignment Session | ryd Portal',
          breadcrumb: 'Session',
          requiredRoles: PAY_OPS_ALL_ROLES,
        },
      },
      {
        path: 'order-amendments/:uuid',
        name: 'pay-operations-order-amendment-detail',
        component: OrderAmendmentDetail,
        props: true,
        meta: {
          title: 'Order Amendment Detail | ryd Portal',
          breadcrumb: 'Amendment Detail',
          requiredRoles: PAY_OPS_ALL_ROLES,
        },
      },
    ],
  },
];
